import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { adminAPI } from "@/services/api";
import { getErrorMessage, safeRender } from "@/utils/errorHelpers";
import { AlertTriangle, CheckCircle, ClipboardCheck, ShieldAlert, Trash2, UserX } from "lucide-react";
import { toast } from "sonner";
import { ADMIN_RANKS, hasFullAdminRank } from "@/utils/adminRanks";

const STATUS_LABELS = {
  pending: "En attente",
  in_review: "En cours",
  resolved: "Traite",
  rejected: "Rejete",
};

export default function ModerationQueuePage({ user }) {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const isFull = hasFullAdminRank(user);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getModerationQueue({ status_filter: statusFilter });
      setData(response.data || {});
    } catch (error) {
      toast.error(getErrorMessage(error, "File de moderation indisponible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const counts = useMemo(() => data?.counts || {}, [data]);
  const deletionRequests = Array.isArray(data?.account_deletion_requests) ? data.account_deletion_requests : [];
  const reports = Array.isArray(data?.moderation_reports) ? data.moderation_reports : [];

  const totalPending = useMemo(() => (
    Number(counts.deletion_pending || 0) + Number(counts.reports_pending || 0)
  ), [counts]);

  const setNote = (id, value) => setNotes((prev) => ({ ...prev, [id]: value }));

  const runAction = async (key, action, successMessage) => {
    setBusyKey(key);
    try {
      await action();
      toast.success(successMessage);
      await loadQueue();
    } catch (error) {
      toast.error(getErrorMessage(error, "Action impossible"));
    } finally {
      setBusyKey("");
    }
  };

  const reviewDeletion = (item) => runAction(
    `review-${item.id}`,
    () => adminAPI.reviewAccountDeletion(item.id, { notes: notes[item.id] || "" }),
    "Demande prise en charge"
  );

  const resolveDeletion = (item) => {
    const finalize = isFull && window.confirm("Finaliser la suppression/anonymisation du compte lie si possible ?");
    runAction(
      `resolve-${item.id}`,
      () => adminAPI.resolveAccountDeletion(item.id, { notes: notes[item.id] || "", finalize_account_deletion: finalize }),
      "Demande traitee"
    );
  };

  const rejectDeletion = (item) => runAction(
    `reject-${item.id}`,
    () => adminAPI.rejectAccountDeletion(item.id, { notes: notes[item.id] || "" }),
    "Demande rejetee"
  );

  const reviewReport = (item) => runAction(
    `review-report-${item.id}`,
    () => adminAPI.reviewModerationReport(item.id, { notes: notes[item.id] || "" }),
    "Signalement pris en charge"
  );

  const resolveReport = (item) => runAction(
    `resolve-report-${item.id}`,
    () => adminAPI.resolveModerationReport(item.id, { notes: notes[item.id] || "" }),
    "Signalement resolu"
  );

  if (loading) return <LoadingState label="Chargement de la file de moderation..." />;

  return (
    <PageShell data-testid="moderation-queue-page">
      <PageHeader
        title="File moderation et conformite"
        description="Traitement des demandes de suppression de compte, signalements de contenu et controles Google Play."
        actions={
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="in_review">En cours</SelectItem>
              <SelectItem value="resolved">Traites</SelectItem>
              <SelectItem value="rejected">Rejetes</SelectItem>
              <SelectItem value="all">Tout</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="grid gap-3 sm:grid-cols-4">
        <Metric title="A traiter" value={totalPending} icon={ShieldAlert} tone="amber" />
        <Metric title="Suppressions" value={counts.deletion_pending || 0} icon={Trash2} tone="red" />
        <Metric title="Signalements" value={counts.reports_pending || 0} icon={AlertTriangle} tone="amber" />
        <Metric title="En cours" value={(counts.deletion_in_review || 0) + (counts.reports_in_review || 0)} icon={ClipboardCheck} tone="green" />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Demandes de suppression" icon={Trash2}>
          {deletionRequests.length === 0 ? (
            <EmptyLine>Aucune demande dans ce filtre.</EmptyLine>
          ) : deletionRequests.map((item) => (
            <QueueCard key={item.id} item={item} notes={notes[item.id] || ""} setNote={(value) => setNote(item.id, value)}>
              <div className="grid gap-2 text-xs text-[#5C625F] sm:grid-cols-2">
                <Info label="E-mail" value={item.email} />
                <Info label="Compte" value={item.user_id || item.user?.id || "Non lie"} />
                <Info label="Nom" value={item.full_name || item.user?.full_name} />
                <Info label="IP" value={item.ip_address} />
              </div>
              <p className="rounded-lg bg-[#F9F7F4] p-3 text-sm leading-6 text-[#424846]">{item.reason || "Aucun motif fourni."}</p>
              <div className="flex flex-wrap gap-2">
                <ActionButton onClick={() => reviewDeletion(item)} busy={busyKey === `review-${item.id}`} disabled={isClosed(item)}>Prendre</ActionButton>
                <ActionButton onClick={() => resolveDeletion(item)} busy={busyKey === `resolve-${item.id}`} disabled={isClosed(item)} tone="success">
                  {isFull ? "Traiter / supprimer" : "Marquer traite"}
                </ActionButton>
                <ActionButton onClick={() => rejectDeletion(item)} busy={busyKey === `reject-${item.id}`} disabled={isClosed(item)} tone="danger">Rejeter</ActionButton>
              </div>
            </QueueCard>
          ))}
        </Section>

        <Section title="Signalements" icon={ShieldAlert}>
          {reports.length === 0 ? (
            <EmptyLine>Aucun signalement dans ce filtre.</EmptyLine>
          ) : reports.map((item) => (
            <QueueCard key={item.id} item={item} notes={notes[item.id] || ""} setNote={(value) => setNote(item.id, value)}>
              <div className="grid gap-2 text-xs text-[#5C625F] sm:grid-cols-2">
                <Info label="Cible" value={`${item.target_type} / ${item.target_id}`} />
                <Info label="Signale par" value={item.reporter?.full_name || item.reporter_id} />
                <Info label="Role" value={item.reporter?.role || item.reporter_role} />
                <Info label="IP" value={item.ip_address} />
              </div>
              <p className="rounded-lg bg-[#F9F7F4] p-3 text-sm leading-6 text-[#424846]">{item.reason}</p>
              {item.target_message && (
                <div className="rounded-lg border border-[#E6E4E0] bg-white p-3 text-xs leading-5">
                  <strong>{item.target_message.sender_name}</strong>: {item.target_message.body}
                </div>
              )}
              {item.target_product && (
                <div className="rounded-lg border border-[#E6E4E0] bg-white p-3 text-xs leading-5">
                  Produit: <strong>{item.target_product.title}</strong> - {item.target_product.author_name || item.target_product.author_id}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <ActionButton onClick={() => reviewReport(item)} busy={busyKey === `review-report-${item.id}`} disabled={isClosed(item)}>Prendre</ActionButton>
                <ActionButton onClick={() => resolveReport(item)} busy={busyKey === `resolve-report-${item.id}`} disabled={isClosed(item)} tone="success">Marquer resolu</ActionButton>
              </div>
            </QueueCard>
          ))}
        </Section>
      </div>
    </PageShell>
  );
}

function Metric({ title, value, icon: Icon, tone }) {
  const toneClass = tone === "red" ? "bg-red-50 text-red-700" : tone === "green" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700";
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs font-semibold uppercase text-[#8A8F8C]">{title}</p>
          <p className="mt-1 text-2xl font-bold text-[#2A4B46]">{value}</p>
        </div>
        <div className={`rounded-full p-2 ${toneClass}`}>
          <Icon size={18} />
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-[#2A4B46]">
          <Icon size={18} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function QueueCard({ item, notes, setNote, children }) {
  return (
    <div className="space-y-3 rounded-xl border border-[#E6E4E0] bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-mono text-xs font-semibold text-[#5C625F]">{item.id}</p>
          <p className="text-xs text-[#8A8F8C]">{formatDate(item.created_at)}</p>
        </div>
        <Badge className={statusClass(item.status)}>{STATUS_LABELS[item.status] || item.status}</Badge>
      </div>
      {children}
      {!isClosed(item) && (
        <Textarea
          value={notes}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder="Note interne de traitement..."
        />
      )}
    </div>
  );
}

function ActionButton({ children, onClick, busy, disabled, tone = "default" }) {
  const className = tone === "danger"
    ? "bg-red-700 text-white hover:bg-red-800"
    : tone === "success"
      ? "bg-emerald-700 text-white hover:bg-emerald-800"
      : "bg-[#2A4B46] text-white hover:bg-[#1f3a36]";
  return (
    <Button type="button" size="sm" onClick={onClick} disabled={disabled || busy} className={className}>
      {busy ? "Traitement..." : children}
    </Button>
  );
}

function Info({ label, value }) {
  return (
    <span className="rounded-lg bg-[#F9F7F4] px-3 py-2">
      <strong className="text-[#2A4B46]">{label}:</strong> {safeRender(value) || "-"}
    </span>
  );
}

function EmptyLine({ children }) {
  return <div className="rounded-xl border border-dashed border-[#D8D1C7] p-6 text-center text-sm text-[#5C625F]">{children}</div>;
}

function statusClass(status) {
  if (status === "resolved") return "bg-emerald-100 text-emerald-800";
  if (status === "rejected") return "bg-red-100 text-red-800";
  if (status === "in_review") return "bg-blue-100 text-blue-800";
  return "bg-amber-100 text-amber-800";
}

function isClosed(item) {
  return ["resolved", "rejected"].includes(item?.status);
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("fr-FR");
  } catch (error) {
    return value;
  }
}
